import { Header, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import * as S from './styles';
import { useDataroomContext } from '../../providers/DataroomProvider';
import DataroomActions from '../DataroomActions';
import { Button } from '~/components';
import DocumentRow from './DocumentRow';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { DataroomDocument } from '~/types/dataroom';
import { SummaryDocumentRow, DealSpreadsheetRow } from './KeyDownloadRows';

export default function DocumentList() {
  const {
    dataroom,
    selectedCategory,
    selectedDoclistCategory,
    editingDocumentTypes,
    setEditingDocumentTypes,
    isSimplifiedUI,
    selectedSubject,
  } = useDataroomContext();

  const { permissions } = usePermissions();

  let documentTypes: DataroomDocument[] | undefined = undefined;
  if (isSimplifiedUI) {
    const additionalDocumentsMap = {} as { [id: string]: boolean };
    const allDocTypes = dataroom?.documents
      ?.reduce((acc, curr) => {
        if (curr.code === 'ADDITIONAL_DOCUMENTS') {
          curr.documentTypes.forEach(documentType => {
            additionalDocumentsMap[documentType.id] = true;
          });
        }
        acc.push(...curr.documentTypes);
        return acc;
      }, [] as DataroomDocument[])
      ?.sort((a, b) => {
        return a.name.localeCompare(b.name);
      }) ?? [];

      documentTypes = [
        ...allDocTypes.filter(documentType => !additionalDocumentsMap[documentType.id]),
        ...allDocTypes.filter(documentType => additionalDocumentsMap[documentType.id])
      ];
  } else {
    documentTypes = dataroom?.documents
      ?.find((category) => category?.id === selectedCategory?.id)?.documentTypes
      ?.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
  }

  const uploadedDocuments = documentTypes?.filter(documentType => {
    return documentType.isSelected && documentType.document; 
  }) ?? [];

  const selectedDocumentTypes = documentTypes?.filter(documentType => {
    if (permissions?.canUpdateDataroom) { 
      return documentType.isSelected && !documentType.document;
    } else {
      return null;
    }
  }) ?? [];

  const unSelectedDocumentTypes = selectedDoclistCategory?.documentTypes?.filter((documentType: any) => { 
    return !documentType.isSelected;
  }).sort((a: any, b: any) => a.name.localeCompare(b.name)) ?? [];

  // Key Downloads are portfolio-level: only show when no per-project subject is selected.
  const isPortfolioRootSubject = !selectedSubject || selectedSubject.type === 'portfolio';
  const showKeyDownloads = isSimplifiedUI && isPortfolioRootSubject && (permissions?.isInvestor || permissions?.isAdmin);

  const subheadingParts: string[] = [];
  if (selectedSubject) {
    subheadingParts.push(
      selectedSubject.type === 'portfolio' ? 'Portfolio-level documents' : selectedSubject.label
    );
  }
  if (!isSimplifiedUI && selectedCategory?.name) {
    subheadingParts.push(selectedCategory.name);
  } else if (isSimplifiedUI && !selectedSubject) {
    subheadingParts.push('Recommended Documents');
  }
  const subheading = subheadingParts.filter(Boolean).join(' › ');

  return (
    <div className="document-list">
      {subheading && (
        <Header as="h3" style={{ marginTop: 0, marginBottom: 'var(--small)' }}>
          {subheading}
        </Header>
      )}
      {!isSimplifiedUI && <div style={{marginBottom: '16px'}}><DataroomActions /></div>}
      {showKeyDownloads && (
        <>
          <SummaryDocumentRow />
          <DealSpreadsheetRow />
        </>
      )}
        <S.DocumentList isEditing={editingDocumentTypes}>
          {uploadedDocuments.length || selectedDocumentTypes.length ? (
            <>
              {uploadedDocuments.map((documentType) => (
                <DocumentRow key={documentType?.id} data={documentType} />
              ))}
              <S.SelectedDocumentTypesRow 
                showSpacing={uploadedDocuments.length > 0}
                isEditing={editingDocumentTypes}
              >
                {selectedDocumentTypes.map((documentType) => (
                  <DocumentRow key={documentType?.id} data={documentType} />
                ))}
              </S.SelectedDocumentTypesRow>
            </>
          ) : !editingDocumentTypes && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 'var(--2x-large) var(--medium)',
              marginTop: 'var(--medium)',
              border: '1px dashed var(--color-grey)',
              borderRadius: '6px',
              color: 'var(--color-charcoal)',
              gap: 'var(--x-small)',
            }}>
              <Icon name="folder open outline" size="huge" style={{ color: 'var(--color-grey)', margin: 0 }} />
              <Header as="h4" style={{ margin: 0, color: 'var(--color-pure-black)' }}>
                Nothing here yet
              </Header>
              <p style={{ margin: 0, maxWidth: '24rem' }}>
                {permissions?.canUpdateDataroom
                  ? 'No documents have been selected for this section yet. Click ‘Edit doc list’ above to add one.'
                  : (
                    <>
                      There are no documents available in this section right now. Check back later. New files will appear here as they’re uploaded and you’ll be notified via email according to your <Link to="/settings/notifications">notification settings</Link>.
                    </>
                  )}
              </p>
              {permissions?.canUpdateDataroom && (
                <Button primary variant="link" onClick={() => setEditingDocumentTypes(true)} style={{ marginTop: 'var(--x-small)' }}>
                  Edit doc list
                </Button>
              )}
            </div>
          )}
        <S.UnselectedDocumentRow isEditing={editingDocumentTypes}>
          {unSelectedDocumentTypes.map((documentType: any) => (
            <DocumentRow key={documentType.id} data={documentType} />
          ))}
        </S.UnselectedDocumentRow>
      </S.DocumentList>
     </div>
  );
}